import React from 'react'
import Text from '../../components/text/Text'
import {IVolumeLeague, LeagueStatus} from '../../apis/volume_league/list'
import {ICard} from './types'
import {STATUS_META, STATUS_ORDER} from './constants'
import {kindTagClass, tfLabel} from './utils'
import img_placeholder from '../../assets/images/placeholder.png'
import img_delete from '../../assets/images/img_delete.png'

interface IProps {
    cards: ICard[]
    editKey: string
    onEdit: (card: ICard) => void
    onChangeStatus: (card: ICard, status: LeagueStatus) => void
    onDelete: (card: ICard) => void
    cardLogo: (l?: IVolumeLeague) => string
    formatUTCDate: (time: number, tz: number) => string
    displayAmount: (amount: number) => string
}

/** 그룹 진입 후의 리그(레코드) 카드 목록 — 종류·대상·기간·상금·갱신/상태/삭제 */
const LeagueCardList: React.FC<IProps> = ({
    cards,
    editKey,
    onEdit,
    onChangeStatus,
    onDelete,
    cardLogo,
    formatUTCDate,
    displayAmount,
}) => {
    return (
        <>
            {cards.map(card => {
                const rep = card.rep
                const isRollingCard = rep?.kind === 'rolling'
                const tfs = [...card.items]
                    .map(l => l?.window_days ?? 0)
                    .filter(Boolean)
                    .sort((a, b) => a - b)
                    .map(tfLabel)
                // 요약 세그먼트 (기간 / 상금 / 갱신 / 보드) — 색상·간격으로 구분
                const periodStr = isRollingCard
                    ? tfs.join(' · ')
                    : `${formatUTCDate(new Date(rep?.start_at || 0).getTime(), 9)} ~ ${formatUTCDate(
                          new Date(rep?.end_at || 0).getTime(),
                          9,
                      )}`
                const prizeStr =
                    !isRollingCard && rep?.prize_pool?.total_amount
                        ? `${displayAmount(rep?.prize_pool?.total_amount)} ${rep?.prize_pool?.currency || ''}`
                        : ''
                const scopeBadge =
                    rep?.scope_type === 'exchange'
                        ? `거래소 기준 · ${(rep?.exchange || '').toUpperCase()}`
                        : `코인 기준 · ${(rep?.symbol || '').toUpperCase()}`
                return (
                    <div
                        key={card.key}
                        onClick={() => onEdit(card)}
                        className={`relative p-4 border border-line02 bg-white rounded-[6px] flex items-center gap-x-3 transition-shadow cursor-pointer ${
                            editKey === card.key
                                ? 'ring-1 ring-inset ring-primary'
                                : 'hover:ring-1 hover:ring-inset hover:ring-primary'
                        }`}>
                        {/* 본문 */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-x-2 w-full">
                                <img
                                    src={cardLogo(rep) || img_placeholder}
                                    onError={e => ((e.target as HTMLImageElement).src = img_placeholder)}
                                    className="w-10 h-10 min-w-[40px] rounded-full object-cover bg-line03"
                                    alt=""
                                />
                                <div className="flex items-center gap-x-2 flex-wrap">
                                    <Text className="text-ti1 font-semibold text-font01 truncate max-w-[260px]">
                                        {rep?.name}
                                    </Text>
                                    <Text
                                        className={`text-ti3 px-2 py-[2px] rounded-[4px] ${kindTagClass(
                                            rep?.kind || 'rolling',
                                        )}`}>
                                        {rep?.kind === 'rolling' ? '상시 순위표' : '기간 대회'}
                                    </Text>
                                </div>
                            </div>
                            <Text className="text-ti3 text-font02 py-[2px] mt-[10px] flex min-w-0 rounded-[4px]">
                                {scopeBadge}
                            </Text>
                            <div className="mt-[10px] flex items-center flex-wrap gap-x-[22px] gap-y-[6px]">
                                <div className="flex items-center gap-x-[6px]">
                                    <Text className="text-ti3 text-font03">기간</Text>
                                    <Text className="text-body3 font-bold text-font01">{periodStr || '-'}</Text>
                                </div>
                                {!isRollingCard && (
                                    <div className="flex items-center gap-x-[6px]">
                                        <Text className="text-ti3 text-font03">상금</Text>
                                        {prizeStr ? (
                                            <Text className="text-body3 font-bold text-font_green">{prizeStr}</Text>
                                        ) : (
                                            <Text className="text-body3 font-bold text-font_red">-</Text>
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center gap-x-[6px]">
                                    <Text className="text-ti3 text-font03">갱신</Text>
                                    <Text className="text-body3 font-bold text-sub_blue">
                                        {rep?.refresh_interval_sec ?? '-'}s
                                    </Text>
                                </div>
                            </div>
                        </div>
                        {/* 상태 드롭다운 (상태 원 + 색상) */}
                        <div onClick={e => e.stopPropagation()} className="flex items-center gap-x-[7px] flex-none pr-[28px]">
                            {(() => {
                                const sMeta = STATUS_META[(rep?.status as LeagueStatus) || 'draft']
                                return (
                                    <span className="relative flex items-center justify-center w-[6px] h-[6px] flex-none">
                                        {sMeta.live && (
                                            <span
                                                className={`absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping ${sMeta.dot}`}
                                            />
                                        )}
                                        <span
                                            className={`relative inline-flex w-[6px] h-[6px] rounded-full ${sMeta.dot}`}
                                        />
                                    </span>
                                )
                            })()}
                            <select
                                value={rep?.status || 'draft'}
                                onChange={e => onChangeStatus(card, e.target.value as LeagueStatus)}
                                className={`text-ti3 px-2 py-[4px] rounded-[5px] border-none outline-none cursor-pointer ${
                                    STATUS_META[(rep?.status as LeagueStatus) || 'draft'].select
                                }`}>
                                {STATUS_ORDER.map(s => (
                                    <option key={s} value={s}>
                                        {STATUS_META[s].label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* 삭제 아이콘 (우측 하단) */}
                        <img
                            src={img_delete}
                            alt="삭제"
                            onClick={e => {
                                e.stopPropagation()
                                onDelete(card)
                            }}
                            className="absolute right-[12px] bottom-[12px] w-[28px] h-[28px] cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                        />
                    </div>
                )
            })}
            {cards.length === 0 && <Text className="text-body2 text-font03">해당 상태의 리그가 없습니다.</Text>}
        </>
    )
}

export default LeagueCardList
